import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { pickWelcome } from "../lib/messages";
import { WavingCat } from "./Cat";

type Props = {
  onAuth: () => void;
};

const PIN = "2010";
const PIN_LENGTH = 4;

export function LoginPage({ onAuth }: Props) {
  const [digits, setDigits] = useState<string[]>(["", "", "", ""]);
  const [shake, setShake] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const message = useMemo(() => pickWelcome(), []);

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  const setDigit = (idx: number, value: string) => {
    // Only allow a single digit per box; clearing is fine too.
    const v = value.replace(/\D/g, "").slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[idx] = v;
      return next;
    });
    if (v && idx < PIN_LENGTH - 1) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const tryAuth = (current: string[]) => {
    if (current.join("").length !== PIN_LENGTH) return;
    if (current.join("") === PIN) {
      onAuth();
    } else {
      setShake(true);
      setError("not quite — try again");
      setTimeout(() => {
        setShake(false);
        setDigits(["", "", "", ""]);
        inputsRef.current[0]?.focus();
      }, 500);
    }
  };

  useEffect(() => {
    if (digits.join("").length === PIN_LENGTH) {
      // Slight delay so the last digit visibly settles before the screen
      // either shakes or transitions away.
      const t = setTimeout(() => tryAuth(digits), 120);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digits]);

  return (
    <div className="relative flex h-screen w-screen flex-col items-center justify-center overflow-hidden bg-canvas">
      {/* Soft pastel halos in the corners — pure decoration */}
      <div className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full bg-lavender-100/60 blur-3xl" />
      <div className="pointer-events-none absolute -right-32 -bottom-32 h-96 w-96 rounded-full bg-peach-100/60 blur-3xl" />
      <div className="pointer-events-none absolute right-12 top-20 h-72 w-72 rounded-full bg-mint-100/40 blur-3xl" />

      <div className="relative z-10 flex w-full max-w-md flex-col items-center px-6 text-center">
        <WavingCat />

        <h1 className="mt-2 text-3xl font-semibold tracking-tight text-ink">
          welcome back<span className="text-lavender-400">.</span>
        </h1>

        <p
          className={clsx(
            "mt-4 max-w-sm text-[15px] leading-relaxed text-ink-soft transition-opacity duration-500"
          )}
          key={message}
        >
          {message}
        </p>

        <div className={clsx("mt-10 flex gap-3", shake && "animate-shake")}>
          {Array.from({ length: PIN_LENGTH }).map((_, i) => (
            <input
              key={i}
              ref={(el) => {
                inputsRef.current[i] = el;
              }}
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={digits[i]}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Backspace" && !digits[i] && i > 0) {
                  inputsRef.current[i - 1]?.focus();
                } else if (e.key === "ArrowLeft" && i > 0) {
                  inputsRef.current[i - 1]?.focus();
                } else if (e.key === "ArrowRight" && i < PIN_LENGTH - 1) {
                  inputsRef.current[i + 1]?.focus();
                }
              }}
              onPaste={(e) => {
                const txt = e.clipboardData.getData("text").replace(/\D/g, "");
                if (txt.length >= PIN_LENGTH) {
                  e.preventDefault();
                  const next = txt.slice(0, PIN_LENGTH).split("");
                  setDigits(next);
                  inputsRef.current[PIN_LENGTH - 1]?.focus();
                }
              }}
              maxLength={1}
              className={clsx(
                "h-14 w-12 rounded-xl border bg-surface text-center text-2xl font-medium tracking-wider text-ink shadow-soft outline-none transition-all",
                error
                  ? "border-blush-200 ring-2 ring-blush-100"
                  : "border-border hover:border-border-strong focus:border-lavender-200 focus:ring-2 focus:ring-lavender-100"
              )}
            />
          ))}
        </div>

        <p
          className={clsx(
            "mt-4 h-4 text-xs transition-opacity",
            error ? "text-blush-400 opacity-100" : "text-ink-mute opacity-0"
          )}
        >
          {error ?? "."}
        </p>

        <p className="mt-12 text-[11px] text-ink-mute">
          enter your 4-digit pin to continue
        </p>
      </div>

      <style>{`
        @keyframes shake-x {
          0%, 100% { transform: translateX(0); }
          15%      { transform: translateX(-6px); }
          30%      { transform: translateX(5px); }
          45%      { transform: translateX(-4px); }
          60%      { transform: translateX(3px); }
          75%      { transform: translateX(-2px); }
        }
        .animate-shake { animation: shake-x 0.5s ease-in-out; }
        @media (prefers-reduced-motion: reduce) {
          .animate-shake { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
